/**
 * Capacitor FCM push bootstrap.
 *
 * Runs ONLY inside the Capacitor Android app (Play Store shell). On plain
 * web browsers this is a no-op — the existing Web Push (VAPID) hook
 * `usePushNotifications` handles that path.
 *
 * Flow on Android app launch:
 *   1. Detect Capacitor + Android.
 *   2. Ask the user for notification permission (Android 13+ shows the
 *      system prompt; older Androids auto-grant).
 *   3. Register with FCM → Capacitor returns a device registration
 *      token.
 *   4. POST that token to `/api/push/fcm/register` so the backend can
 *      target this device.
 *   5. Wire two listeners:
 *      • `pushNotificationReceived` — a push arrived while the app is
 *        FOREGROUND. Android will NOT show a system notification in
 *        that case; we surface an in-app toast instead.
 *      • `pushNotificationActionPerformed` — the user tapped a
 *        notification (app was BACKGROUND / KILLED). We deep-link to
 *        the URL from the notification's `data.url` field.
 */
import { toast } from "sonner";
import api from "@/lib/api";

function isCapacitor() {
  try {
    return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch (_) { return false; }
}

let _initedOnce = false;

export async function initCapacitorPush() {
  if (_initedOnce) return;
  _initedOnce = true;

  if (!isCapacitor()) return;                    // Web/PWA — skip.
  // Both Android (FCM) and iOS (APNs) are supported. The plugin
  // abstracts the transport away — from JS the flow is identical:
  //   requestPermissions → register → `registration` event fires
  //   with the platform's device token.
  const platform = window.Capacitor?.getPlatform?.() || "unknown";
  if (platform !== "android" && platform !== "ios") return;

  let PushNotifications;
  try {
    ({ PushNotifications } = await import("@capacitor/push-notifications"));
  } catch (err) {
    console.warn("[fcm] plugin import failed:", err);
    return;
  }

  // ── Purge any legacy Web Push (VAPID) subscription first ──────────
  // Existing installs from before v2.0.12 subscribed to BOTH transports
  // (usePushNotifications ran inside the Capacitor WebView back then).
  // The Web Push side is fatal on Android app: the service-worker
  // `notificationclick` handler can only route via
  // `clients.openWindow(url)` → Chrome/Samsung Internet, since the
  // Capacitor WebView isn't a `windowClient` the SW can `focus()`.
  // Result on tap: the URL opens in the SYSTEM BROWSER instead of the
  // app. Rip that subscription out on every launch so upgrading users
  // stop getting the duplicate browser-opening notification.
  try {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      const reg = await navigator.serviceWorker.getRegistration("/");
      if (reg) {
        const existing = await reg.pushManager.getSubscription();
        if (existing) {
          const endpoint = existing.endpoint;
          await existing.unsubscribe();
          try { await api.post("/push/unsubscribe", { endpoint }); }
          catch (_) { /* server row will be pruned via 410 on next broadcast */ }
          console.log("[fcm] purged legacy Web Push subscription");
        }
        // Also unregister the SW so no future push arrives via that
        // path even if some other codepath tries to resubscribe.
        try { await reg.unregister(); } catch (_) { /* non-fatal */ }
      }
    }
  } catch (err) {
    console.warn("[fcm] legacy webpush purge failed (non-fatal):", err);
  }

  try {
    // ── Permission ──────────────────────────────────────────────
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== "granted") {
      const req = await PushNotifications.requestPermissions();
      if (req.receive !== "granted") {
        console.log("[fcm] permission denied — silent skip");
        return;
      }
    }

    // ── Register with FCM (Android) / APNs (iOS) ──────────────
    // The plugin's `register()` doesn't return the token directly —
    // instead it fires the `registration` event asynchronously. Wire
    // that listener BEFORE calling register().
    await PushNotifications.addListener("registration", async (info) => {
      const token = info?.value;
      if (!token) return;
      try {
        if (platform === "ios") {
          // APNs path — stored in `user_devices` collection.
          await api.post("/notifications/register-device", {
            token,
            platform: "ios",
            app_version: window.Capacitor?.getAppVersion?.() || undefined,
          });
        } else {
          // Android FCM path — kept on the pre-existing endpoint so
          // legacy tokens don't have to be migrated.
          await api.post("/push/fcm/register", {
            token,
            platform: "android",
            app_version: window.Capacitor?.getAppVersion?.() || undefined,
          });
        }
        console.log(`[push] ${platform} token registered with backend`);
      } catch (err) {
        console.warn("[push] token upload failed:", err?.response?.status || err);
      }
    });

    await PushNotifications.addListener("registrationError", (err) => {
      console.warn("[fcm] registration error:", err);
    });

    // ── Foreground receipt: show a toast (Android silently drops the
    //    system notification if the app is foreground). ──────────
    await PushNotifications.addListener("pushNotificationReceived", (notification) => {
      const title = notification?.title || notification?.data?.title || "New notification";
      const body = notification?.body  || notification?.data?.body  || "";
      // sonner supports { description } for the secondary line.
      try { toast.info(title, { description: body }); }
      catch { toast(title + (body ? " — " + body : "")); }
    });

    // ── Tap on a notification → deep-link route ────────────────
    await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      // Normalise the tap-target URL. The backend sends the URL in
      // `notification.data.url` — sometimes it's an absolute
      // https://astrokuberchat.com/... URL (composed by the admin),
      // sometimes a relative /path. In BOTH cases we want to route
      // in-WebView so the tap opens the same page inside the app,
      // never a browser.
      const raw = action?.notification?.data?.url;
      if (typeof raw !== "string" || !raw) return;

      // Case 1: relative path — assign directly.
      if (raw.startsWith("/")) {
        window.location.assign(raw);
        return;
      }
      // Case 2: absolute astrokuberchat.com URL — strip the origin
      // and route the path inside the WebView.
      try {
        const u = new URL(raw);
        const host = u.hostname.replace(/^www\./, "");
        if (host === "astrokuberchat.com") {
          window.location.assign((u.pathname || "/") + u.search + u.hash);
          return;
        }
      } catch (_) {
        /* fall through */
      }
      // Case 3: some other absolute URL — as a safety net, still route
      // in-app to home (never hand off to an external browser from a
      // push tap; that would be a jarring UX).
      window.location.assign("/");
    });

    await PushNotifications.register();
  } catch (err) {
    console.warn("[fcm] bootstrap error:", err);
  }
}
